import React from 'react';
import useHttp from '../../../hooks/http.hook';
import MiniLineChart from './MiniLineChart';
import './DailyStatsCharts.css';

const CHART_PLOT_PX = 210;
const CHART_BAR_HEIGHTS = [
  [46, 62, 38, 74, 54, 88, 42, 96],
  [68, 44, 80, 36, 72, 58, 92, 50],
  [34, 70, 48, 86, 40, 64, 78, 56],
];

function DailyStatsChartsSkeleton() {
  return (
    <div className="daily-stats-loading" aria-busy="true" aria-label="Загрузка графиков">
      <div className="daily-stats-forecast daily-stats-forecast--skeleton" aria-hidden="true">
        <div className="daily-stats-skel daily-stats-skel--onDark daily-stats-skel--forecastTitle" />
        <div className="daily-stats-forecastItems">
          {[0, 1, 2].map((i) => (
            <div key={i} className="daily-stats-forecastSkelItem">
              <div className="daily-stats-skel daily-stats-skel--onDark daily-stats-skel--forecastLabel" />
              <div className="daily-stats-skel daily-stats-skel--onDark daily-stats-skel--forecastValue" />
            </div>
          ))}
        </div>
      </div>
      <div className="daily-stats-charts" aria-hidden="true">
        {CHART_BAR_HEIGHTS.map((heights, chartIndex) => (
          <div key={chartIndex} className="daily-chart daily-chart--skeleton">
            <div className="daily-chart-head">
              <div className="daily-stats-skel daily-stats-skel--chartTitle" />
              <div className="daily-stats-skel daily-stats-skel--legend" />
            </div>
            <div className="daily-stats-skelPlot">
              {heights.map((height, barIndex) => (
                <span key={barIndex} className="daily-stats-skelBarCol">
                  <span
                    className="daily-stats-skelBar"
                    style={{ height: `${Math.round((height / 100) * CHART_PLOT_PX)}px` }}
                  />
                </span>
              ))}
            </div>
            <div className="daily-chart-prediction">
              <div className="daily-stats-skel daily-stats-skel--prediction" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const MIN_PREDICTION_DAYS = 8;

function DailyStatsCharts({ branchKey, active = false }) {
  const { requestWithMeta } = useHttp();
  const [loading, setLoading] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [prediction, setPrediction] = React.useState(null);
  const [predictionLoading, setPredictionLoading] = React.useState(false);
  const [fetchError, setFetchError] = React.useState(false);
  const [retryTick, setRetryTick] = React.useState(0);

  const branch = branchKey || 'store';

  React.useEffect(() => {
    if (!active) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setFetchError(false);
      setPrediction(null);
      setPredictionLoading(false);
      try {
        const res = await requestWithMeta(
          `/operator/stats/daily/branch/${encodeURIComponent(branch)}`,
          'GET'
        );
        if (cancelled) return;
        if (!res?.ok) {
          setRows([]);
          setPrediction(null);
          setPredictionLoading(false);
          setFetchError(true);
          return;
        }
        const raw = res?.data?.data;
        const list = Array.isArray(raw) ? raw : [];
        const normalized = list.map((r) => ({
          day: String(r?.day ?? ''),
          quantity: Number(r?.quantity ?? 0),
          revenue: Number(r?.revenue ?? 0),
          orders: Number(r?.orders ?? 0),
        }));
        setRows(normalized);
        setPrediction(null);
        setPredictionLoading(normalized.length >= MIN_PREDICTION_DAYS);
        setFetchError(false);
      } catch (_) {
        if (!cancelled) {
          setRows([]);
          setPrediction(null);
          setPredictionLoading(false);
          setFetchError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, branch, requestWithMeta, retryTick]);

  React.useEffect(() => {
    if (!active || loading || rows.length < MIN_PREDICTION_DAYS) return undefined;

    let settled = false;
    const worker = new Worker(new URL('../predicting/predicting.worker.js', import.meta.url));

    const finish = (next) => {
      if (settled) return;
      settled = true;
      setPrediction(next);
      setPredictionLoading(false);
    };

    worker.onmessage = (event) => {
      const payload = event.data;
      const next = payload?.prediction;
      if (payload?.ok && next && typeof next === 'object') {
        finish({
          quantity: Number(next.quantity),
          revenue: Number(next.revenue),
          orders: Number(next.orders),
        });
        return;
      }
      finish(null);
    };

    worker.onerror = () => {
      finish(null);
    };

    worker.postMessage(rows);

    return () => {
      settled = true;
      worker.terminate();
    };
  }, [active, loading, rows]);

  const nfInt = React.useMemo(() => new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }), []);
  const nfMoney = React.useMemo(
    () => new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }),
    []
  );

  const onRetry = () => {
    setRetryTick((t) => t + 1);
  };

  const days = rows.map((r) => r.day);
  const quantities = rows.map((r) => (Number.isFinite(r.quantity) ? r.quantity : 0));
  const revenues = rows.map((r) => (Number.isFinite(r.revenue) ? r.revenue : 0));
  const orders = rows.map((r) => (Number.isFinite(r.orders) ? r.orders : 0));

  if (!active) return null;

  return (
    <section
      id="analytics-section-charts"
      className="daily-stats"
      aria-label="Дневная статистика"
    >
      <div className="daily-stats-panel">
          {loading ? (
            <DailyStatsChartsSkeleton />
          ) : fetchError ? (
            <div className="daily-stats-fallback">
              <p className="daily-stats-fallbackText">Не удалось загрузить данные.</p>
              <button type="button" className="daily-stats-retry" onClick={onRetry}>
                Повторить
              </button>
            </div>
          ) : rows.length === 0 ? (
            <p className="daily-stats-emptyPeriod">Нет продаж за последние ~30 дней.</p>
          ) : (
            <>
              <div className="daily-stats-forecast">
                <div className="daily-stats-forecastTitle">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M3 17l6-6 4 4 8-8" stroke="#7FE0AE" strokeWidth="2.4" strokeLinecap="round" />
                    <path d="M15 7h6v6" stroke="#7FE0AE" strokeWidth="2.4" strokeLinecap="round" />
                  </svg>
                  Прогноз на завтра
                </div>
                {predictionLoading ? (
                  <div className="daily-stats-forecastItems" aria-busy="true" aria-label="Считаем прогноз">
                    {['Продано', 'Выручка', 'Заказы'].map((label) => (
                      <div key={label} className="daily-stats-forecastItem">
                        <span>{label}</span>
                        <span className="daily-stats-skel daily-stats-skel--onDark daily-stats-skel--forecastValue" />
                      </div>
                    ))}
                  </div>
                ) : (
                <div className="daily-stats-forecastItems">
                  <div className="daily-stats-forecastItem">
                    <span>Продано</span>
                    <b>
                      {Number.isFinite(prediction?.quantity) ? nfInt.format(Math.round(prediction.quantity)) : '—'}
                      <em>шт</em>
                    </b>
                  </div>
                  <div className="daily-stats-forecastItem">
                    <span>Выручка</span>
                    <b>
                      {Number.isFinite(prediction?.revenue) ? nfMoney.format(Math.round(prediction.revenue)) : '—'}
                      <em>сом</em>
                    </b>
                  </div>
                  <div className="daily-stats-forecastItem">
                    <span>Заказы</span>
                    <b>
                      {Number.isFinite(prediction?.orders) ? nfInt.format(Math.round(prediction.orders)) : '—'}
                      <em>шт</em>
                    </b>
                  </div>
                </div>
                )}
              </div>
              <div className="daily-stats-charts">
                <MiniLineChart
                  title="Продано товаров, шт."
                  days={days}
                  values={quantities}
                  formatValue={(v) => nfInt.format(Math.round(v))}
                  predictionRaw={prediction?.quantity}
                  predictionUnit="шт."
                  predictionLoading={predictionLoading}
                />
                <MiniLineChart
                  title="Выручка"
                  days={days}
                  values={revenues}
                  formatValue={(v) => nfMoney.format(Math.round(v))}
                  predictionRaw={prediction?.revenue}
                  predictionUnit="сом"
                  predictionLoading={predictionLoading}
                />
                <MiniLineChart
                  title="Количество заказов"
                  days={days}
                  values={orders}
                  formatValue={(v) => nfInt.format(Math.round(v))}
                  predictionRaw={prediction?.orders}
                  predictionLoading={predictionLoading}
                />
              </div>
            </>
          )}
      </div>
    </section>
  );
}

export default DailyStatsCharts;
